import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { SaveBar } from "../../../../components/utils/SaveBar";
import { fetchReport, fetchReportChecklist } from "../../../../api/service/report";
import {
  FullReportTitle,
  FullReportAddress,
  ReportStatus,
  ReportAppointmentApprovedStatus,
  ReportInvoiceStatus,
  ReportMustBeRectified,
} from "../../../../components/dashboard/service/reports/utils";
import {
  CheckBadgeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  UsersIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import ChecklistItemLinesCompactV2 from "../../../../components/dashboard/quality/checklists/checklistItemLinesCompactV2";
import { updateReportChecklist } from "../../../../api/service/report";
import { PMNotice } from "../../../../components/utils/notifications";
import { ChecklistTypeSelector } from "../../../../components/dashboard/quality/checklists/checklistTypeSelector";
import { createReportChecklist } from "../../../../api/service/report";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { CopyToClipboard } from "../../../../components/utils/Div";
import { mapsServiceUrl, serviceAgreementPath } from "../../../../components/Utils";

export const ReportChecklistPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const settings = useSelector((state) => state.session.currentUser.settings);
  const currentUser = useSelector((state) => state.session.currentUser);

  const [report, setReport] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(true);
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState([]);
  const [checklistTypeSelectorOpen, setChecklistTypeSelectorOpen] = useState(false);
  const [apiErrors, setApiErrors] = useState([]);

  // Determine if user can edit this report
  const canEdit =
    report?.user_can_edit_with_rights !== false ||
    report?.user_can_edit !== false;

  // Check if user is admin
  const isAdmin = useMemo(() => {
    return currentUser?.roles?.some((r) => r.name === "admin") || false;
  }, [currentUser?.roles]);

  // Load report
  useEffect(() => {
    if (!id) return;

    setIsLoading(true);
    setError(false);
    setErrors([]);

    const params = {
      fields: "*,contact.*,user_can_edit_with_rights,approved,not_approved,updated_after_process,process_note,approval_status",
    };

    fetchReport(id, params)
      .then((data) => {
        setReport(data?.data);
        setIsLoading(false);
      })
      .catch(() => {
        toast.error("Kunne ikke laste rapport");
        setError(true);
        setIsLoading(false);
      });
  }, [id]);

  // Load checklist when report is loaded
  useEffect(() => {
    if (!report?.id) return;

    setIsLoadingChecklist(true);
    setApiErrors([]);

    fetchReportChecklist(report.id, {
      fields: "id,title,description,status,is_items_editable,items.*,checklist_type.id,checklist_type.displayTitle",
    })
      .then((response) => {
        // Check if response has errors (from backend)
        if (response?.errors) {
          // If 404 error, it means no checklist exists yet - that's fine
          if (response.errors.some(err => err.includes("does not have a checklist") || err.includes("404"))) {
            setChecklist(null);
            setApiErrors([]);
          } else {
            setApiErrors(response.errors);
            setChecklist(null);
          }
          setIsLoadingChecklist(false);
          return;
        }
        
        // Response can be either response.data (from axios) or just data (from our wrapper)
        const checklistData = response?.data || response;
        if (checklistData && checklistData.id) {
          // Ensure items is always an array
          if (!checklistData.items || !Array.isArray(checklistData.items)) {
            checklistData.items = [];
          }
          setChecklist(checklistData);
        } else {
          setChecklist(null);
        }
        setIsLoadingChecklist(false);
      })
      .catch((error) => {
        console.error("Error loading checklist:", error);
        // If 404, it means no checklist exists yet - that's fine
        if (error?.response?.status === 404 || error?.status === 404) {
          setChecklist(null);
          setApiErrors([]);
        } else {
          const errorMessage = error?.response?.data?.errors?.join(", ") ||
                              error?.response?.data?.error ||
                              error?.message ||
                              "Kunne ikke laste sjekkliste";
          setApiErrors([errorMessage]);
          setChecklist(null);
        }
        setIsLoadingChecklist(false);
      });
  }, [report?.id]);

  const submitCreateChecklist = async (selectedChecklistType, checklistTitle) => {
    try {
      const response = await createReportChecklist({
        report: {
          id: report?.id,
          checklist_type_id: selectedChecklistType,
          ...(checklistTitle
            ? { checklist_title: checklistTitle }
            : {}),
        },
        fields: "id,checklist",
      });

      if (response.data.id && response.data.checklist) {
        toast.success("Sjekkliste opprettet");
        // Reload checklist
        const checklistResponse = await fetchReportChecklist(report.id, {
          fields: "id,title,description,status,is_items_editable,items.*,checklist_type.id,checklist_type.displayTitle",
        });
        const checklistData = checklistResponse?.data || checklistResponse;
        if (checklistData && checklistData.id) {
          if (!checklistData.items || !Array.isArray(checklistData.items)) {
            checklistData.items = [];
          }
          setChecklist(checklistData);
        }
        setChecklistTypeSelectorOpen(false);
      }
    } catch (err) {
      toast.error("Kunne ikke opprette sjekkliste");
      console.error("Error creating checklist:", err);
    }
  };

  const handleOnRefresh = (updatedReport) => {
    if (updatedReport) {
      setReport((prevReport) => ({
        ...prevReport,
        ...updatedReport,
      }));
    }
  };

  if (isLoading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={errors} />;
  }

  if (!report?.id) {
    return <ResourceEmpty />;
  }

  const headerMeta = [
    {
      component: <ReportStatus report={report} />,
      label: "Status",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      component: <ReportAppointmentApprovedStatus report={report} />,
      label: "AppointmentStatus",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: ["new", "ongoing"].includes(report.status) ? false : true,
      copy: false,
    },
    {
      component: <ReportMustBeRectified rectify={report?.must_be_rectified} />,
      label: "AppointmentStatus",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: ["accomplished"].includes(report.status) ? false : true,
      copy: false,
    },
    {
      label: report?.customer?.customer_number + " " + report?.customer?.name,
      description: "",
      path: "/dashboard/customers/" + report?.customer?.id,
      action: null,
      icon: UsersIcon,
      textColor: "text-teal-700",
      hidden: report?.customer?.id ? false : true,
      copy: false,
    },
    {
      label: report?.project?.displayTitle,
      description: "",
      path: "/dashboard/projects/" + report?.project?.id,
      action: null,
      icon: BuildingOfficeIcon,
      textColor: "text-teal-700",
      hidden: report?.project?.id ? false : true,
      copy: false,
    },
    {
      label: (
        <CopyToClipboard
          path={mapsServiceUrl(
            FullReportAddress(report),
            settings?.preferred_maps_service
          )}
          content={FullReportAddress(report)}
          type="popup"
        />
      ),
      description: "",
      path: null,
      action: null,
      icon: MapPinIcon,
      textColor: "text-teal-700",
      hidden: report?.delivery_address || report.address ? false : true,
      copy: true,
    },
    {
      label: (
        <CopyToClipboard
          path={"tel:" + report?.customer?.mobile}
          content={report?.customer?.mobile}
          type="popup"
        />
      ),
      description: "",
      path: null,
      action: null,
      icon: PhoneIcon,
      textColor: "text-teal-700",
      hidden: report?.customer?.mobile ? false : true,
      copy: true,
    },
    {
      label: "Serviceavtale " + report?.agreement?.agreement_number,
      description: "",
      path: serviceAgreementPath({ serviceAgreement: report?.agreement }),
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: report?.agreement?.id ? false : true,
      copy: true,
    },
  ];

  return (
    <>
      <DashboardHeader
        subtitle="Sjekkliste"
        title={FullReportTitle(report)}
        meta={headerMeta}
      >
        <div className="mx-auto max-w-5xl px-2 sm:px-4 py-3 sm:py-5">
          {/* Report Info Section */}
          <div className="mb-4 sm:mb-6 bg-white shadow sm:rounded-lg">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <ClipboardDocumentIcon className="h-6 w-6 text-teal-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Sjekkliste for rapport
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {report?.report_number} - {report?.report_type?.displayTitle}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Checklist Content */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-3 sm:px-6 py-4 sm:py-6">
              {!canEdit && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Kun visning
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          Denne sjekklisten kan ikke redigeres. Du kan se innholdet,
                          men ikke lagre endringer.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* API Errors */}
              {apiErrors.length > 0 && checklist && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Feil ved lagring
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <ul className="list-disc list-inside">
                          {apiErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {isLoadingChecklist ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Laster sjekkliste...</span>
                </div>
              ) : apiErrors.length > 0 && !checklist ? (
                <>
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Feil ved lasting av sjekkliste
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <ul className="list-disc list-inside">
                            {apiErrors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-gray-600">
                    <p>Kunne ikke laste sjekkliste. Vennligst prøv igjen senere eller kontakt support hvis problemet vedvarer.</p>
                  </div>
                </>
              ) : !checklist && !isLoadingChecklist ? (
                <>
                  <div className="py-5 px-5 rounded-md shadow-xl">
                    <PMNotice
                      title="Sjekkliste mangler"
                      description="Rapporten er ikke knyttet til sjekkliste. Velg en sjekkliste type nedenfor for å opprette en."
                    />

                    <div className="mt-6">
                      <ChecklistTypeSelector
                        open={checklistTypeSelectorOpen}
                        onClose={() => setChecklistTypeSelectorOpen(false)}
                        report={report}
                        onChange={(checklistType, { title }) => {
                          submitCreateChecklist(checklistType.id, title);
                        }}
                      />
                      
                      <button
                        type="button"
                        onClick={() => setChecklistTypeSelectorOpen(true)}
                        disabled={!canEdit}
                        className="w-full inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                        Velg sjekkliste type
                      </button>
                    </div>
                  </div>
                </>
              ) : checklist ? (
                <div className="space-y-6">
                  <ChecklistItemLinesCompactV2
                    checklist={checklist}
                    items={checklist?.items || []}
                    reportId={report?.id}
                    editable={canEdit && checklist?.is_items_editable}
                    disabled={
                      !canEdit ||
                      (checklist?.is_items_editable !== undefined &&
                        !checklist?.is_items_editable)
                    }
                    isAdmin={isAdmin}
                    validationErrors={{}}
                    updateChecklistItems={async (id, payload) => {
                      return await updateReportChecklist(id, payload);
                    }}
                    onItemsUpdate={(updatedItems) => {
                      // Update checklist state when items are updated
                      const updatedChecklist = {
                        ...checklist,
                        items: updatedItems,
                      };
                      setChecklist(updatedChecklist);
                      
                      // Notify parent component about the update
                      if (report) {
                        handleOnRefresh({
                          ...report,
                          checklist: {
                            id: updatedChecklist.id,
                            items: updatedItems,
                          },
                        });
                      }
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </DashboardHeader>
      
      <SaveBar
        alwaysShow={true}
        onBack={() => navigate(`/dashboard/service/reports/${report?.id}`)}
        backLabel="Tilbake til rapport"
      />
    </>
  );
};

